import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { Person } from "@/types";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { ApolloError } from "@apollo/client/errors";
import { UPDATE_CONTACT_PREFERENCE_MUTATION } from "@/features/person/queries";
import {
  AddressWithFacilityName,
  buildContactAddressListItem,
  buildContactPreferenceAddressOptions
} from "@/features/person/utils/utils";
import { formattedAddress, getAddressIconName, getAddressTypeLabel } from "@/lib/addressHelpers";
import Banner from "@/components/Banner";
import MenuDropdown from "@/components/MenuDropdown";
import ListItem from "@/components/ListItem";

interface UpdatePreferencesModalProps {
  person: Person;
  open?: boolean;
  onClose(): void;
  onSuccess(updatedPerson: Partial<Person>): void;
}

const getAddressLabel = ({ addressType, facilityName }: AddressWithFacilityName) => {
  return getAddressTypeLabel(addressType, { includeFacilityPrefix: true, facilityName });
};

const UpdatePreferencesModal = ({ person, open, onClose, onSuccess }: UpdatePreferencesModalProps) => {
  const [mutate, { loading }] = useMutation(UPDATE_CONTACT_PREFERENCE_MUTATION);
  const [bannerError, setBannerError] = useState("");

  const addressOptions = buildContactPreferenceAddressOptions(person);

  const [addressId, setAddressId] = useState(person.contactAddressId);
  const selectedAddress =
    addressOptions.find((address) => address.id === person.contactAddressId) ??
    (person.contactAddress?.id === person.contactAddressId ? person.contactAddress : undefined);
  const currentContactPreference =
    person.contactAddressId && selectedAddress
      ? buildContactAddressListItem(selectedAddress, person.contactAddressId, person.employments)
      : null;

  // Reset state when modal opens or when person's contact preference changes
  // This ensures the modal always reflects the current contact preference
  useEffect(() => {
    if (open) {
      setAddressId(person.contactAddressId);
      setBannerError("");
    }
  }, [open, person.contactAddressId]);

  const onSubmit = async () => {
    try {
      const {
        data: {
          updatePerson: { success, message }
        }
      } = await mutate({
        variables: {
          input: {
            id: person.id,
            contactAddressId: addressId
          }
        }
      });
      if (success) {
        onSuccess({ contactAddressId: addressId });
        onClose();
      } else {
        throw new Error(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Update Preferences"
      className="w-[648px] max-w-[90vw]"
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="preferences-cancel-button">
            Cancel
          </Button>
          <Button disabled={loading || !addressId} onClick={onSubmit} testId="preferences-save-button">
            Save
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-4">
          <Banner inline variant="error" description="The update failed." subText={bannerError} />
        </div>
      )}
      <div className="flex flex-col gap-4">
        <p className="text-title-medium">Contact Preference Details</p>
        {currentContactPreference && <ListItem {...currentContactPreference} />}

        <MenuDropdown
          options={addressOptions}
          value={addressOptions.find((address) => address.id === addressId)}
          onSelect={(address) => setAddressId(address.id)}
          label="Contact Preference"
          getLabel={(address) => getAddressLabel(address)}
          renderItem={(address) => (
            <ListItem
              iconName={getAddressIconName(address.addressType)}
              iconBackground
              overline={getAddressLabel(address)}
              headline={formattedAddress(address)}
              compact
            />
          )}
        />
      </div>
    </Dialog>
  );
};

export default UpdatePreferencesModal;
